'use client'

import { useEffect, useState } from 'react'

/**
 * The decorative background of the auth card's right-hand panel.
 *
 * A still frame always renders as the base layer, and the looping video is
 * mounted on top only when two conditions hold. Both are checked in JS rather
 * than CSS because neither can be expressed as a media query on an element
 * that must not merely be *hidden* but must never load or decode at all:
 *
 *   1. The viewport is >= md. The panel is `hidden md:flex`, so on a phone the
 *      video would download and decode for something nobody can see. The GIF
 *      this replaced got that for free (a background-image on a display:none
 *      element is never fetched); a <video> element does not.
 *   2. The user has not asked for reduced motion. `motion-reduce:hidden` would
 *      hide the video but leave it playing and decoding, which misses the
 *      point — WCAG 2.2.2 wants the motion gone, not just invisible.
 *
 * Both start false so the server render and first paint are the still image.
 * Reduced-motion users therefore never mount a video at all, and nobody sees a
 * frame of motion before the preference is applied.
 */
export default function HeroLoop() {
  const [showVideo, setShowVideo] = useState(false)

  useEffect(() => {
    const wide = window.matchMedia('(min-width: 768px)')
    const still = window.matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => setShowVideo(wide.matches && !still.matches)

    apply()
    wide.addEventListener('change', apply)
    still.addEventListener('change', apply)
    return () => {
      wide.removeEventListener('change', apply)
      still.removeEventListener('change', apply)
    }
  }, [])

  return (
    <div aria-hidden='true' className='absolute inset-0 overflow-hidden'>
      <div className="absolute inset-0 bg-[url('/login-image.png')] bg-cover bg-center brightness-75" />
      {showVideo && (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload='auto'
          poster='/login-image.png'
          className='absolute inset-0 h-full w-full object-cover brightness-75'
        >
          <source src='/login-loop.webm' type='video/webm' />
          <source src='/login-loop.mp4' type='video/mp4' />
        </video>
      )}
    </div>
  )
}
